import React, { forwardRef, useEffect, useState } from 'react';
import { Radio, Checkbox } from '@maxtropy/components';
import { StateTimeType } from '../../utils';
import { Space, Spin } from 'antd';
import { EnergyMonitorContext } from '../../';
import {
  apiV2HighEnergyDeviceDeviceIndicatorItemPost,
  V2HighEnergyDeviceDeviceIndicatorItemPostResponse,
  apiV2HighEnergyDeviceDeviceTreePost,
  V2HighEnergyDeviceDeviceTreePostResponse,
} from '@maxtropy/device-customer-apis-v2';
import styles from '../index.module.scss';
import { RadioChangeEvent } from 'antd/lib';
import useLatestRequest from '../../useLatestRequest';

export type TreeProps = Exclude<V2HighEnergyDeviceDeviceTreePostResponse['list'], undefined>[number];
export type SurveyProps = Exclude<V2HighEnergyDeviceDeviceIndicatorItemPostResponse['list'], undefined>[number];
// 单值
export interface DeviceMonitorSingleValue {
  type: number;
  id: number | undefined; // 永远是 number
  measurementType?: number;
  label?: string;
}

// 多值
interface DeviceMonitorMultiValue {
  type: number;
  id: number[] | null | undefined; // 永远是数组
  measurementType?: number;
  label?: string;
}
// 联合
export type DeviceMonitorValue = DeviceMonitorSingleValue | DeviceMonitorMultiValue;
export interface SurveyFlatItem {
  key: string;
  label: string;
  value: DeviceMonitorValue;
}

export type SurveyFlatProps = SurveyFlatItem[];

export interface IObjectCompare {
  onDeviceMonitorChange?: (list: DeviceMonitorSingleValue[]) => void;
  onDeviceChange?: (list: number[]) => void;
}

const MAX = 6;
const DeviceCompare = (props: IObjectCompare, ref: any) => {
  const { deviceLedgerId } = React.useContext(EnergyMonitorContext);
  const { onDeviceChange, onDeviceMonitorChange } = props;
  const [checkedList, setCheckedList] = useState<number[]>([deviceLedgerId as number]);
  const [radioValue, setRadioValue] = useState([]);
  const [deviceOp, setDeviceOp] = useState<TreeProps['deviceLedgerInfo']>([]);
  const [surveyOp, setSurveyOp] = useState<SurveyFlatProps>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { runLatest: runLatestTree } = useLatestRequest();
  const { runLatest: runLatestIndicators, cancelLatest: cancelLatestIndicators } = useLatestRequest();

  useEffect(() => {
    const flatDevice: TreeProps['deviceLedgerInfo'] = [];

    runLatestTree(() => apiV2HighEnergyDeviceDeviceTreePost({}), {
      onSuccess: res => {
        res.list?.forEach(item => {
          if (item.deviceLedgerInfo && Array.isArray(item.deviceLedgerInfo)) {
            flatDevice.push(...item.deviceLedgerInfo);
          }
        });
        setDeviceOp(flatDevice);
      },
    });
    onDeviceChange?.(checkedList);
  }, [runLatestTree]);

  useEffect(() => {
    if (!checkedList.length) {
      cancelLatestIndicators();
      setSurveyOp([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const inventoryIds = checkedList.filter(id => id !== undefined) as number[];
    runLatestIndicators(() => apiV2HighEnergyDeviceDeviceIndicatorItemPost({ inventoryIds }), {
      onSuccess: res => {
        const surveyOp: SurveyFlatProps = (res.list ?? []).flatMap(item => {
          if (item.type === StateTimeType.ENERGY && item.energyMediumItem) {
            return item.energyMediumItem.map(d => ({
              key: `${item.type}-${d.id}`,
              label: d.name ?? '',
              value: { type: item.type!, id: d.id, measurementType: d.measurementType },
            }));
          }

          if (item.type === StateTimeType.MONITORING && item.groupIndicator) {
            return (item.groupIndicator ?? []).flatMap<SurveyFlatItem>(d => {
              if (d.groupType !== 0) {
                const ids = (d.propertyList ?? []).map(p => p.propertyId!);
                return [
                  {
                    key: `${item.type}-${d?.propertyList?.[0]?.propertyId ?? ''}`,
                    label: d.showName ?? '',
                    value: {
                      type: item.type!,
                      id: ids,
                      measurementType: d?.propertyList?.[0]?.measurementType,
                    },
                  },
                ];
              } else {
                return (
                  d.propertyList?.map(p => ({
                    key: `${item.type}-${p.propertyId ?? ''}`,
                    label: p.propertyName ?? '',
                    value: { type: item.type!, id: p.propertyId, measurementType: p.measurementType },
                  })) ?? []
                );
              }
            });
          }

          return {
            key: `${item.type}`,
            label: item.name ?? '',
            value: { type: item.type!, id: undefined },
          };
        });

        setSurveyOp(surveyOp);
      },
      onSettled: () => setLoading(false),
    });
  }, [cancelLatestIndicators, checkedList, runLatestIndicators]);

  const onChange = (list: number[]) => {
    if (checkedList.length >= MAX) return;
    setCheckedList(list);
    onDeviceChange?.(list);
  };

  const onChangeRadioValue = (elment: RadioChangeEvent) => {
    const e = elment.target.value;
    setRadioValue(e);
    const baseValue = { ...e.value, label: e.label };
    let valueWithLabel: Array<{ type: any; id: number; label: string; measurementType?: number }> = []; // 明确为数组类型

    if (Array.isArray(baseValue.id)) {
      // 如果id是数组，遍历生成对象数组
      valueWithLabel = baseValue.id.map((singleId: number) => ({
        type: baseValue.type,
        id: singleId,
        label: e.label,
        measurementType: baseValue.measurementType,
      }));
    } else {
      // 如果id是单个值，包装成数组
      valueWithLabel = [
        {
          type: baseValue.type,
          id: baseValue.id as number, // 假设id为number类型，若可能为其他类型可调整
          label: e.label,
          measurementType: baseValue.measurementType,
        },
      ];
    }
    // if (Array.isArray(valueWithLabel.id)) {
    //   valueWithLabel.id?.map((singleId: number) => ({
    //     type: valueWithLabel.type, // 保留原type值
    //     id: singleId, // 每个元素对应一个id
    //     label: e.label,
    //   }));
    // }
    // aa.push(valueWithLabel);
    if (e) onDeviceMonitorChange?.(valueWithLabel);
  };

  return (
    <div className={styles.compareSection}>
      <div className="section device-section">
        <p className={styles.sectionTitle}>
          选择对比设备 <span className="moreNumber">最多选择6个</span>
        </p>
        <div className={styles.deviceList}>
          <Checkbox.Group onChange={onChange} layout="vertical" defaultValue={checkedList}>
            {deviceOp?.map(i => (
              <Checkbox
                key={i.id}
                value={i.id}
                disabled={i.id === deviceLedgerId || (checkedList.length >= MAX && !checkedList.includes(i.id!))}
              >
                {i.assetDeviceName}
              </Checkbox>
            ))}
          </Checkbox.Group>
        </div>
      </div>
      <Spin spinning={loading}>
        <div className="section metric-section">
          <p className={styles.sectionTitle}>选择监测指标</p>

          <div className={styles.metricList}>
            <Radio.Group onChange={onChangeRadioValue} value={radioValue}>
              <Space size="middle" direction="vertical">
                {surveyOp?.map(i => (
                  <Radio key={i.key} value={i}>
                    {i.label}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </div>
        </div>
      </Spin>
    </div>
  );
};

export default forwardRef(DeviceCompare);
